import store from '@/store';
import i18n from '@/js/i18n';
import { bus } from '@/main';

import api from '@/services/api';
import promo from '@/services/api/compte/promo';
import session from '@/services/api/compte/session';
import { isJelMobile, getHomePageUrl, getPromoFromQueryParams } from '@/helpers/AppHelpers';
import MessageHelpers from '@/helpers/MessageHelpers';
import { isProductSource2eChance } from '@/helpers/ProductSourceHelpers';
import { PageMapping } from '@/helpers/PageMappingHelpers';
import { obtenirSiteIds } from '@/services/api/compte/sites';
import { playerPaymentProfileStatus } from '@/helpers/ProfileHelpers';
import { returnToHome } from '@/helpers/RouteHelpers';
import { parseData } from '@/helpers/StringHelpers';

export default {
  async creerCompte(recaptchaToken) {
    const newRegFlow = process.env.VUE_APP_NEW_REG_FLOW === 'true';
    if (newRegFlow) {
      if (!session.sessionEstOuverte()) {
        session.cleanUpSession();
        returnToHome();
      }
      try {
        const data = {
          dossierClient: {
            clientMigrated: store.state.dossier?.clientMigrated,
            versionDonnees: store.state.dossier?.versionDonnees,
          },
          custUUID: store.state.dossier?.custUUID,
          contactable: store.state.compte.recevoirCommunication,
          channel: isJelMobile() ? 'M' : 'I',
        };
        const response = await souscrireInfolettre(data);
        if (isProductSource2eChance()) {
          store.commit('compte/RESET');
          throw { url: PageMapping.COMPTE_CONFIRMATION_2E_CHANCE.siteUrl };
        }
        return response;
      } catch (error) {
        if (error?.url) {
          throw error;
        }
        throw { url: PageMapping.ERREUR_GENERIQUE.siteUrl };
      }
    } else {
      // Creation du compte avec OB
      const state = store.state.compte;
      const datas = {
        profile: {
          // ACCEPT en maj. est nécessaire pour les tests PaySafe
          firstName: state.prenom == 'Accept' ? 'ACCEPT' : state.prenom,
          lastName: state.nom,
          title: state.genre,
          dateOfBirth: store.getters['compte/getDateOfBirth'](),
          preferredLanguage: i18n.locale,
          emailAddress: state.courriel,
          phoneNumber: {
            number: store.getters['compte/getPhoneNumber'](),
            type: 'HOME',
          },
        },
        credentials: {
          password: state.motDePasse,
        },
        marketing: {
          marketingCommunication: state.recevoirCommunication,
          acquisitionSource: store.getters['compte/getAcquisitionSource'](),
        },
        promotion: {
          code: state.codePromo || '',
        },
        termsAndConditions: {
          accepted: true, // Acceptation implicite des CUS
        },
      };

      // Passer l'adresse si elle a été saisie par le consommateur (2è chance par exemple)
      if (state.choixAdresseValidation.numero) {
        datas.profile.address = {
          addressLine1: state.choixAdresseValidation.numero,
          addressLine2: state.choixAdresseValidation.rue,
          addressLine3: state.choixAdresseValidation.appartement,
          city: state.choixAdresseValidation.ville,
          postcode: state.choixAdresseValidation.codePostal,
        };
      } else if (state.adresse.numero) {
        datas.profile.address = {
          addressLine1: state.adresse.numero,
          addressLine2: state.adresse.rue,
          addressLine3: state.adresse.appartement,
          city: state.adresse.ville,
          postcode: state.adresse.codePostal,
        };
      }

      const headers = {
        'cache-control': 'no-cache',
        'Content-Type': 'application/json',
        'g-recaptcha': recaptchaToken,
        'x-channel': isJelMobile() ? 'M' : 'I',
      };
      return api
        .call('account/v2/players', 'creerCompte', 'POST', {
          withCredentials: true,
          headers,
          data: JSON.stringify(datas),
        })
        .then(() => session.ouvrirSession(state.courriel, state.motDePasse))
        .then(() => {
          if (isProductSource2eChance()) {
            /**
             * On efface le store après le parcours d'inscription 2e chance
             * Spécifications:
             * - TTP13701 (https://testtrackweb.loto-quebec.com/ttweb/index.html#default/18/issues/13768/)
             */
            store.commit('compte/RESET');
            // Redirection des clients 2e Chance après inscription
            // Spécification : https://lotoquebec.atlassian.net/browse/SWSPC-1769
            throw { url: PageMapping.COMPTE_CONFIRMATION_2E_CHANCE.siteUrl };
            // obtenirSiteIds()
            //   .then((response) => {
            //     if (response.data.URLRetourInscription) {
            //       window.location.replace(response.data.URLRetourInscription);
            //     }
            //   })
            //   .catch((error) => {
            //     console.error("Erreur à l'initialisation de l'url", error);
            //   });
          }
          store.commit('compte/RESET');
          store.commit('promo/RESET');
        })
        .catch((error) => {
          if (error.url) {
            throw error;
          }

          if (error.message && error.message == 'CONNEXION') {
            throw { url: PageMapping.ERREUR_INSCRIPTION.siteUrl };
          }

          if (error.response) {
            if (error.response.status === 422) {
              if (error.response.data.detailCause == 'DUPLICATE_ACCOUNT') {
                store.commit('compte/RESET');
                store.commit('profile/RESET');
                store.commit('promo/RESET');
                throw {
                  url: PageMapping.COMPTE_DOUBLON_2E_CHANCE.siteUrl,
                };
              }

              if (error.response.data.detailCause == 'VALIDATION_ERROR') {
                const siteUrl = isJelMobile()
                  ? PageMapping.COURRIEL.siteUrl
                  : PageMapping.INFOS_PERSONNELLES.siteUrl;
                // Courriel déjà existant
                MessageHelpers.obtenirMessage('MSG01-1-02')
                  .then((msgResp) => {
                    const msgDetails = MessageHelpers.nettoyerMessageSpanP(msgResp.msgDetail);
                    const msg = {
                      msgCode: 'MSG01-1-02',
                      msgTitre: msgResp.msgTitre,
                      msgDetail: msgDetails,
                      msgType: 'erreur',
                    };
                    store.commit('message/ADD_MESSAGE_FLASH', msg);
                    throw { url: siteUrl, message: msg };
                  })
                  .catch((error) => {
                    console.log('Erreur de récupération du message!', error);
                  });
              }
            }
          }

          throw { url: PageMapping.ERREUR_INSCRIPTION.siteUrl };
        });
    }
  },

  async validerUniciteCourriel(recaptchaToken) {
    const headers = {
      'g-recaptcha': recaptchaToken,
    };
    const params = {
      email: store.state.compte.courriel,
    };
    await api
      .call('portal/portal_api/validation/emails', 'validerUniciteCourriel', 'POST', {
        headers,
        params,
      })
      .then((response) => {
        // Courriel déjà existant, on affiche le message flash MSG01-1-02
        // en restant sur la même page(courriels/info-personnelles).
        // Pour tout autre statut (200 / 400 / 401 / 500) : permettre au client de continuer
        // sans afficher aucun message erreur/informatif
        if (response.status === 202) {
          // On récupère le message pour l'afficher sur la page
          MessageHelpers.obtenirMessage('MSG01-1-02')
            .then((msgResp) => {
              const msgDetails = MessageHelpers.nettoyerMessageSpanP(msgResp.msgDetail);
              const msg = {
                msgCode: 'MSG01-1-02',
                msgTitre: msgResp.msgTitre,
                msgDetail: msgDetails,
                msgType: 'erreur',
              };
              store.commit('message/ADD_MESSAGE_FLASH', msg);
              bus.$emit('afficherMessage', msg);
              return msgResp;
            })
            .catch((error) => {
              console.log('Erreur de récupération du message!', error);
            });

          throw { url: '' };
        } else {
          store.commit('message/ADD_MESSAGE_FLASH', null);
          bus.$emit('afficherMessage', null);
        }
      })
      .catch((error) => {
        if (error.url === '') {
          throw error;
        } else {
          console.warn('Echec lors de la validation unicité courriel');
        }
      });
  },

  /**
   * Sauvegarde de la limite de dépôt
   */
  async sauvegarderLimiteDepot() {
    return new Promise((resolve, reject) => {
      try {
        const weekLimit = store.getters['compte/getLimitValue']('deposit', 'week');
        const isEmpty = weekLimit.actuelValue === null || weekLimit.futureValue === null;
        if (!isEmpty) {
          resolve(true);
        } else {
          reject(false);
        }
      } catch (error) {
        reject({ url: PageMapping.ERREUR_GENERIQUE.siteUrl });
      }
    });
  },

  async aiguillerDateDeNaissance() {
    const codePromo = store.state.compte.codePromo || getPromoFromQueryParams();

    return new Promise((resolve, reject) => {
      if (isProductSource2eChance()) {
        return reject({ url: PageMapping.ADRESSE_2E_CHANCE.siteUrl });
      }

      const promoAction = codePromo
        ? promo.reclamerPromo(codePromo)
        : promo.reclamerPromoGenerique();

      return promoAction
        .then(() => reject({ url: PageMapping.OFFRE.siteUrl }))
        .catch(() => resolve('continue'));
    });
  },
  async getInfoCompteCanafe() {
    return new Promise((resolve, reject) => {
      let profileComplet = {};
      const withCredentials = {
        withCredentials: true,
      };
      api
        .call('account-api/players/info-account', 'getAccount', 'GET', {
          withCredentials,
        })
        .then((response) => {
          if (response.data.errors) {
            throw response.data.errors;
          } else {
            this.getOccupation().then((respOccupation) => {
              profileComplet = response.data;
              profileComplet.occupation = respOccupation;
              resolve(profileComplet);
            });
          }
        })
        .catch(() => {
          traiterRetourConnexion().then((response) => {
            reject(response);
          });
        });
      //
    });
  },

  async getOccupation() {
    return new Promise((resolve, reject) => {
      const withCredentials = {
        withCredentials: true,
      };

      api
        .call('ce-portal/portal_api/account/occupation', 'getOccupation', 'GET', {
          withCredentials,
        })
        .then((data) => {
          resolve(data.data.response);
        });
      //
    });
  },

  async setProfileUpdateStatusConfirmed() {
    const withCredentials = {
      withCredentials: true,
    };
    await api
      .call(
        'ce-portal/portal_api/account/profile-update-status/confirmed',
        'setProfileUpdateStatus',
        'POST',
        { withCredentials }
      )
      .then(async () => {
        await traiterRetourConnexion()
          .then((response) => {
            throw { url: response.url };
          })
          .catch((error) => {
            throw error;
          });
      })
      .catch((error) => {
        if (error.url) {
          throw { url: error.url };
        }
        throw { url: PageMapping.ERREUR_CONFIRMATION_CANAFE };
      });
  },

  async setProfileUpdateStatusSeen() {
    return new Promise((resolve, reject) => {
      const withCredentials = {
        withCredentials: true,
      };
      api
        .call(
          'ce-portal/portal_api/account/profile-update-status/seen',
          'setProfileUpdateStatus',
          'POST',
          { withCredentials }
        )
        .then((response) => {
          if (response.data.errorId) {
            traiterRetourConnexion().then((response) => {
              reject(response);
            });
          } else {
            resolve(response);
          }
        })
        .catch((error) => {
          traiterRetourConnexion().then((response) => {
            reject(response);
          });
        });
    });
  },

  async getProfileUpdateStatusSeen() {
    return new Promise((resolve, reject) => {
      const withCredentials = {
        withCredentials: true,
      };
      api
        .call(
          'ce-portal/portal_api/account/profile-update-status',
          'getProfileUpdateStatus',
          'GET',
          { withCredentials }
        )
        .then((response) => {
          if (response.data.errorId) {
            reject(response);
          } else {
            resolve(response);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // Tâche Magnolia appelée par le bouton Continuer dans /compte/identite-reprise
  async refreshSalesforceProfile() {
    return loadPlayerPaymentProfile({
      depositInfo: false,
      withdrawInfo: false,
      forceRefresh: true,
    });
  },
};

/**
 * Obtenir les informations nominatives du client depuis Salesforce
 * via le service jel-module / lqAccount.
 *
 * HTTP 200 → client trouvé, retourne les données nominatives
 * HTTP 404 → client non trouvé dans Salesforce
 * Autre  → erreur inattendue
 */

export async function obtenirInfosSalesforce() {
  const { custUUID } = store.state.dossier;
  const params = custUUID === null ? {} : { idClient: custUUID };

  return new Promise((resolve, reject) => {
    api
      .call('account-api/v3/sf/players-lite', 'obtenirInfosSalesforce', 'GET', {
        withCredentials: true,
        params,
      })
      .then((response) => {
        console.log('obtenirInfosSalesforce', response);
        if (response.data && response.data.status == 'ERROR') {
          reject('empty');
        } else {
          resolve(response);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export async function mettreAJourDossierSalesforce() {
  const state = store.state.compte;
  const data = {
    ConsentementCommunication: null,
    ContactAdresse: {
      NumeroCivique: state.address?.numero,
      Rue: state.address?.rue,
      NumeroAppartement: state.address?.appartement,
      Ville: state.address?.ville,
      CodePostal: state.address?.codePostal,
      IndRefusPropositionAdresse: null,
    },
    Profession: {
      Profession: state.choixProfession?.idProfession,
      SituationProfessionnelle: state.choixProfession?.idSituationProfessionnelle,
      SecteurActivite: state.choixProfession?.secteurActivite,
      DateMAJProfession: null,
    },
  };
  const headers = {
    'cache-control': 'no-cache',
    'Content-Type': 'application/json',
    'x-lq-versiondonnees': store.state.dossier.versionDonnees,
  };
  try {
    await api
      .call('account-api/v3/sf/players', 'mettreAJourProfilClient', 'PATCH', {
        withCredentials: true,
        headers,
        param: {
          idClient: sessionStorage.getItem('lq_client_uuid'),
        },
        data: JSON.stringify(data),
      })
      .then((response) => {
        store.commit('dossier/SET_DOSSIER_VERSION_DONNEES', response.versionDonnees);
        throw { url: PageMapping.IDENTITE.siteUrl };
      });
    store.commit('compte/RESET');
    store.commit('promo/RESET');
  } catch (error) {
    if (error?.url) {
      throw error;
    }
    throw {
      url: PageMapping.ERREUR_GENERIQUE.siteUrl,
    };
  }
}

/**
 * Document.referrer est utilisé parce que
 * nous arrivons généralement de l'extérieur de AppVue
 */
export async function traiterRetourConnexion() {
  return new Promise((resolve, reject) => {
    obtenirSiteIds()
      .then((response) => {
        let urlRetour = getHomePageUrl();
        if (response && response.data.URLRetourConnexion) {
          urlRetour = response.data.URLRetourConnexion;
        } else if (
          document.referrer &&
          !/\/erreur|\/error|\/connexion|\/login/i.test(document.referrer)
        ) {
          urlRetour = document.referrer;
        }
        resolve({ url: urlRetour });
      })
      .catch(() => {
        reject({
          url: getHomePageUrl(),
        });
      });
  });
}

export async function processSalesforceClientMigrated() {
  try {
    const { data } = await obtenirInfosSalesforce();
    if (data?.IdClient) {
      // Client trouvé dans Salesforce
      store.commit('compte/SET_USER_SF_INFO', data);
      store.commit('dossier/SET_DOSSIER_CUSTUUID', data.IdClient);
      store.commit('dossier/SET_DOSSIER_VERSION_DONNEES', data.VersionDonnees);
      store.commit('dossier/SET_DOSSIER_CLIENT_MIGRATED', true);
    } else {
      store.commit('compte/SET_USER_SF_INFO', null);
      store.commit('dossier/SET_DOSSIER_CUSTUUID', null);
      store.commit('dossier/SET_DOSSIER_VERSION_DONNEES', null);
      store.commit('dossier/SET_DOSSIER_CLIENT_MIGRATED', false);
    }
  } catch (error) {
    throw { url: PageMapping.ERREUR_GENERIQUE.siteUrl };
  }
}

export async function souscrireInfolettre(params) {
  const data = params || {
    idClient: store.state.dossier.custUUID,
    versionDonnees: store.state.dossier.versionDonnees,
    niveauInscription: isJelMobile() ? 'M' : 'I',
  };
  return new Promise((resolve, reject) => {
    api
      .call('players/newsletter', 'souscrireInfolettre', 'POST', {
        data,
      })
      .then(
        (response) => {
          if (response.data.status == 'ERROR') {
            reject(response.data);
          } else {
            // Nouvelle version données après newsletter coché ou non coché
            const versionDonnees = parseData(response)?.versionDonnees;
            if (versionDonnees) {
              store.commit('dossier/SET_DOSSIER_VERSION_DONNEES', versionDonnees);
            }
            resolve(response.data);
          }
        },
        (error) => {
          reject(error);
        }
      )
      .catch((error) => {
        reject(error);
      });
  });
}

export async function loadPlayerPaymentProfile(data) {
  const playerPaymentProfile = await store
    .dispatch('profile/getProfileDetails', data)
    .then((response) => {
      if (response.status == 'ERROR') {
        throw { url: PageMapping.ERREUR_PROFIL.siteUrl };
      }
      if (response.playerPaymentProfile?.status?.includes(playerPaymentProfileStatus.SF_LOADING)) {
        throw { url: PageMapping.ERREUR_PAGE_INDISPONIBLE.siteUrl };
      }
      if (response.playerPaymentProfile) {
        return response.playerPaymentProfile;
      }
      return response;
    })
    .catch((error) => {
      if (error.url) {
        throw error;
      }
      throw { url: PageMapping.ERREUR_PROFIL.siteUrl };
    });

  store.commit('compte/SET_INFO_FROM_PLAYER_PAYMENT_PROFILE', playerPaymentProfile);
  return playerPaymentProfile;
}
